import AppStateService from '@ajs/services/AppStateService';
import { Injectable } from '@angular/core';
import { PENDO_GIVE_FEEDBACK_GUIDE_ID, PENDO_NOT_AVAILABLE_MSG, PENDO_RESOURCE_CENTER_GUIDE_ID } from '@app/core/models/constants/pendo.constants';
import { UserFeatureFlag } from '@app/core/models/enums/user-feature-flag.enum';
import { SurfaceTier } from '@app/core/models/types/surface-tier.type';
import { BigCommerceModeStateService } from '@app/core/services/bigcommerce-mode-state.service';
import { LinkService } from '@app/core/services/link.service';
import { LogService } from '@app/core/services/log.service';
import { ForethoughtService } from '@app/core/services/forethought.service';
import { NavGroup } from '@app/modules/nav/models/interfaces/nav-group.interface';
import { faArrowDownToBracket, faCubes, faGrid2, faHome, faQuestionCircle, faShoppingCart } from '@fortawesome/pro-solid-svg-icons';

/**
 * This service creates the navigation groups for Surface mode.
 * The naming, grouping, permissions, and available links are distinctly different that Feedonomics Classic mode.
 */
@Injectable({
    providedIn: 'root'
})
export class SurfaceNavGroupService {
    constructor(
        private readonly appStateService: AppStateService,
        private readonly bigCommerceSelfService: BigCommerceModeStateService,
        private readonly forethoughtService: ForethoughtService,
        private readonly linkService: LinkService,
        private readonly logService: LogService
    ) { }

    /**
     * These are nav groupings for BigCommerce Self Service Mode.
     * @private
     */
    public getSurfaceNavGroups(): NavGroup[] {
        return [
            {
                id: 'dashboard',
                icon: faHome,
                label: 'Dashboard',
                link: null, // todo: this page does not exist yet
                requiredPermissions: {
                    hasPermission: (): boolean => {
                        return false;  // Non-destructively hides option for all users
                    }
                }
            },
            {
                id: 'data',
                icon: faCubes,
                label: 'Products',
                link: this.linkService.baseDataLink,
                requiredPermissions: {},
                children: []
            },
            {
                id: 'exports',
                icon: faGrid2,
                label: 'Channels',
                link: this.linkService.exportsListLink,
                requiredPermissions: {},
                children: [
                    {
                        id: 'attribute-mapping',
                        label: 'Attribute mapping',
                        link: this.linkService.attributeMappingLink,
                        showUpgradeBadge: (): SurfaceTier | null => {
                            if (!this.bigCommerceSelfService.showBcSsPaid) {
                                return 'retail';
                            }
                        },
                        upgradeFeature: 'attribute-mapping',
                        requiredPermissions: {}
                    },
                    {
                        id: 'taxonomy',
                        label: 'Taxonomy',
                        link: this.linkService.categoryTaxonomyLink,
                        showUpgradeBadge: (): SurfaceTier | null => {
                            if (!this.bigCommerceSelfService.showBcSsPaid) {
                                return 'retail';
                            }
                        },
                        upgradeFeature: 'taxonomy',
                        requiredPermissions: {}
                    },
                    {
                        id: 'export-runs',
                        label: 'Sync history',
                        link: this.linkService.exportRunsLink,
                        requiredPermissions: {
                            hasPermission: (): boolean => {
                                return false;
                            }
                        }
                    },
                    {
                        id: 'reports-auth',
                        label: 'Channel connections',
                        link: this.linkService.reportsAuthorizationLink,
                        requiredPermissions: {
                            hasPermission: (): boolean => {
                                return false;
                            }
                        }
                    }
                ]
            },
            {
                id: 'feed-amp-orders',
                icon: faShoppingCart,
                label: 'Orders',
                link: this.linkService.feedAmpPageLink,
                requiredPermissions: {
                    hasPermission: (): boolean => {
                        return this.appStateService.getUser().hasFeatureEnabled(UserFeatureFlag.SURFACE_FEED_AMP);
                    }
                }
            },
            {
                id: 'imports',
                icon: faArrowDownToBracket,
                label: 'Data sources',
                link: this.linkService.importsLink,
                requiredPermissions: {}
            },
            {
                id: 'help',
                icon: faQuestionCircle,
                label: 'Help',
                link: this.linkService.selfServiceHelp,
                requiredPermissions: {},
                children: [
                    {
                        id: 'help-center',
                        label: 'Help center',
                        link: [this.linkService.bcSelfServiceSupportLink],
                        isExternalLink: true,
                        requiredPermissions: {}
                    },
                    {
                        id: 'forethought-support',
                        label: 'Live chat',
                        onClick: (): void => {
                            this.forethoughtService.openWidget();
                        },
                        requiredPermissions: {}
                    },
                    {
                        id: 'pendo-resource-center',
                        label: 'Resource center',
                        onClick: (): void => {
                            if ('pendo' in globalThis && typeof pendo?.showGuideById === 'function') {
                                pendo.showGuideById(PENDO_RESOURCE_CENTER_GUIDE_ID);
                            } else {
                                this.logService.error(PENDO_NOT_AVAILABLE_MSG);
                            }
                        },
                        requiredPermissions: {}
                    },
                    {
                        id: 'pendo-give-feedback',
                        label: 'Give feedback',
                        onClick: (): void => {
                            if ('pendo' in globalThis && typeof pendo?.showGuideById === 'function') {
                                pendo.showGuideById(PENDO_GIVE_FEEDBACK_GUIDE_ID);
                            } else {
                                this.logService.error(PENDO_NOT_AVAILABLE_MSG);
                            }
                        },
                        requiredPermissions: {}
                    }
                ]
            }

        ];
    }
}
